import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { FormPage } from '@/ui/FormPage';
import { FormSection } from '@/ui/FormSection';
import { PageHeader } from '@/ui/PageHeader';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { CampoDePerfil } from './CampoDePerfil';
import { novaChave, useConvidar, useEntrega, useOpcoes } from './dados';
import { avisoDeEntrega } from './rotulos';

interface Erros {
  nome?: string;
  email?: string;
  perfil?: string;
  recorte?: string;
}

/**
 * `/admin/usuarios/convidar`: o convite em tela própria. A pessoa recebe um
 * e-mail para confirmar o endereço, criar a senha e cadastrar o segundo fator;
 * o convite vale 48 horas. A alçada (quem pode dar qual perfil) vem da API.
 *
 * A chave de idempotência nasce com o formulário e só muda quando ele muda: se
 * a resposta se perder e a pessoa clicar de novo, a API devolve o mesmo
 * convite, sem mandar dois e-mails.
 */
export function ConvidarUsuarioPage() {
  const navigate = useNavigate();
  const opcoes = useOpcoes();
  const convidar = useConvidar();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [perfil, setPerfil] = useState('');
  const [recorteId, setRecorteId] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [falha, setFalha] = useState<string | null>(null);
  const chave = useRef(novaChave());
  // Para onde vai o convite neste ambiente: conferido antes de enviar, para a
  // tela não prometer um e-mail que não chega.
  const [emailAssentado, setEmailAssentado] = useState('');
  useEffect(() => {
    const tempo = window.setTimeout(() => setEmailAssentado(email.trim().toLowerCase()), 400);
    return () => window.clearTimeout(tempo);
  }, [email]);
  const entrega = useEntrega(emailAssentado);
  const aviso = entrega.data ? avisoDeEntrega(entrega.data.entrega, emailAssentado) : null;

  function mudou<T>(definir: (v: T) => void) {
    return (valor: T) => {
      chave.current = novaChave();
      definir(valor);
    };
  }

  function conferir(): Erros {
    const e: Erros = {};
    if (nome.trim().split(/\s+/).length < 2) e.nome = 'Informe o nome completo.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Informe um e-mail válido.';
    const escolhido = opcoes.data?.perfis.find((p) => p.codigo === perfil);
    if (!escolhido) e.perfil = 'Escolha o perfil de acesso.';
    if (escolhido && escolhido.tenant !== 'TEG' && !recorteId) {
      e.recorte =
        escolhido.tenant === 'DRE'
          ? 'Escolha a diretoria regional.'
          : 'Escolha a empresa credenciada.';
    }
    return e;
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setFalha(null);
    const encontrados = conferir();
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) return;
    convidar.mutate(
      {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
        perfil,
        ...(recorteId ? { recorteId } : {}),
        chaveIdempotencia: chave.current,
      },
      {
        onSuccess: (r) => {
          toast.success(r.mensagem);
          navigate(`/admin/usuarios/${r.id}`);
        },
        onError: (erro) =>
          setFalha(erro instanceof ErroApi ? erro.aviso : 'Não foi possível enviar o convite.'),
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Convidar pessoa"
        description="A pessoa recebe um e-mail para criar a senha e o segundo fator. O convite vale 48 horas."
      />
      <EstadoDaTela
        carregando={opcoes.isPending}
        erro={opcoes.error}
        aoTentarDeNovo={() => void opcoes.refetch()}
        descricao="os perfis de acesso"
      >
        <FormPage
          onSubmit={enviar}
          isSaving={convidar.isPending}
          submitDisabled={entrega.data?.entrega === 'retido'}
          submitLabel={convidar.isPending ? 'Enviando convite' : 'Enviar convite'}
          submitIcon={<Send className="mr-2 h-4 w-4" aria-hidden="true" />}
          backTo="/admin/usuarios"
          maxWidth="max-w-4xl"
        >
          {falha && (
            <Alert variant="destructive">
              <AlertDescription>{falha}</AlertDescription>
            </Alert>
          )}
          <FormSection title="Quem é a pessoa" icon={Mail}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="convite-nome">Nome completo</Label>
                <Input
                  id="convite-nome"
                  value={nome}
                  autoComplete="off"
                  aria-invalid={Boolean(erros.nome)}
                  aria-describedby={erros.nome ? 'convite-nome-erro' : undefined}
                  onChange={(e) => mudou(setNome)(e.target.value)}
                />
                {erros.nome && (
                  <p id="convite-nome-erro" className="text-sm text-destructive-text">
                    {erros.nome}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="convite-email">E-mail</Label>
                <Input
                  id="convite-email"
                  type="email"
                  value={email}
                  autoComplete="off"
                  aria-invalid={Boolean(erros.email)}
                  aria-describedby={erros.email ? 'convite-email-erro' : 'convite-email-ajuda'}
                  onChange={(e) => mudou(setEmail)(e.target.value)}
                />
                {erros.email ? (
                  <p id="convite-email-erro" className="text-sm text-destructive-text">
                    {erros.email}
                  </p>
                ) : (
                  <p id="convite-email-ajuda" className="text-xs text-muted-foreground">
                    É o endereço de entrada e para onde vai o convite. Depois, não muda por aqui.
                  </p>
                )}
                {aviso && (
                  <Alert>
                    <Info className="h-4 w-4" aria-hidden="true" />
                    <AlertDescription>
                      {aviso}{' '}
                      {entrega.data?.entrega === 'retido'
                        ? 'O convite não pode ser enviado neste ambiente: ele só sai onde o envio de e-mail estiver ligado.'
                        : 'O convite é registrado, mas não chega à pessoa.'}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </FormSection>
          <FormSection title="O que a pessoa pode fazer">
            {opcoes.data && (
              <CampoDePerfil
                opcoes={opcoes.data}
                perfil={perfil}
                recorteId={recorteId}
                aoMudarPerfil={(p) => {
                  mudou(setPerfil)(p);
                  setRecorteId('');
                }}
                aoMudarRecorte={mudou(setRecorteId)}
                erroDoPerfil={erros.perfil}
                erroDoRecorte={erros.recorte}
              />
            )}
          </FormSection>
        </FormPage>
      </EstadoDaTela>
    </div>
  );
}

export default ConvidarUsuarioPage;
